import AppContext from 'App/AppContext';
import { isSameDirectory } from 'models/identity/identityProvider';
import Tenant from 'models/tenants/tenant';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { appRoles } from 'services/Auth/appRoles';
import { forgetSignInReturnPath, rememberSignInReturnPath } from 'services/Auth/signInReturnPath';
import { isValidGuid, normalizeGuid } from 'utils/guid';

//the query parameter of a mail link that names the organization of its page
const tenantLinkParam = 'tenant';

//
// A mail link names the organization of its page: {web}/approvals/{id}?tenant={tenantId},
// {web}/history/{executionId}?tenant={tenantId}, {web}/skills/{skillId}/schedules?tenant={tenantId} and
// {web}/skills?view=proposals&tenant={tenantId} (phase 10), {web}/skills?view=proposals&skill={skillId}&tenant={tenantId}
// and {web}/skills/{skillId}?tenant={tenantId} (the improvement requests, phase 62). When the signed-in user is in
// another organization and the named one is in their organization list, the session switches to it as the account
// menu does (switchOrg for another
// directory, switchOrgUnit for an organizational unit of the same directory) and the page opens after the switch. An
// organization outside the list opens the page as it is: the approval page says it was not found and to switch.
// The parameter is removed from the address (replace) once handled. It only chooses the session; the API decides
// what the user may see. A signed-out user keeps the parameter through the sign-in (SignInRedirect keeps the query).
// Returns true while a switch is about to start or runs, so the routes show the loader instead of loading the page
// in the organization the user leaves.
//
export const useTenantLink = (): boolean => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  // the location whose link started a switch: a switch that lands elsewhere is not repeated
  const [attemptedKey, setAttemptedKey] = useState<string | undefined>(undefined);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const isSignedIn = appContext.isAuthenticated && !appContext.isAuthInProgress;
  const user = appContext.user;

  const params = new URLSearchParams(location.search);
  const hasTenantParam = params.has(tenantLinkParam);
  const rawTenantId = params.get(tenantLinkParam)?.trim();
  const linkTenantId = rawTenantId && isValidGuid(rawTenantId) ? normalizeGuid(rawTenantId) : undefined;

  //the organization to switch to: another one than the current, in the user's list, offered by the account menu
  const getSwitchTarget = (): Tenant | undefined => {
    if (!isSignedIn || !linkTenantId) return undefined;
    if (user.tenant.id.toLowerCase() === linkTenantId) return undefined;

    const tenant = user.login.tenants.find((tn) => tn.id.toLowerCase() === linkTenantId);
    if (!tenant) return undefined;

    //like TenantList: an organizational unit of the own directory only where the user holds a license, unless the
    //user is an organization admin
    const isOrgAdmin = user.login.userSystemRoles?.includes(appRoles.OrgAdmin) === true;
    if (isSameDirectory(tenant, user.tenant) && tenant.currentUserLicensed !== true && !isOrgAdmin) return undefined;

    return tenant;
  };

  const target = getSwitchTarget();
  const startsSwitch = target !== undefined && attemptedKey !== location.key;

  const switchTo = async (tenant: Tenant, returnPath: string) => {
    let keptReturnPath: string | undefined = undefined;
    try {
      if (isSameDirectory(tenant, user.tenant)) {
        await appContext.switchOrgUnit(tenant);
      } else {
        //a switch of directory can leave the app for the identity provider and land on the root: the page is kept
        //for after it (useSignInReturn), and forgotten when the switch ends in place, completed or failed, so a later
        //sign-in in this tab does not open a page of the organization the switch named
        rememberSignInReturnPath(returnPath);
        keptReturnPath = returnPath;
        await appContext.switchOrg(tenant);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      if (keptReturnPath) forgetSignInReturnPath(keptReturnPath);
      setIsSwitching(false);
    }
  };

  useEffect(() => {
    if (!isSignedIn || isSwitching || !hasTenantParam) return;

    const remaining = new URLSearchParams(location.search);
    remaining.delete(tenantLinkParam);
    const search = remaining.toString();
    const path = location.pathname + (search ? `?${search}` : '') + location.hash;

    if (!startsSwitch || !target) {
      navigate(path, { replace: true });

      return;
    }

    setAttemptedKey(location.key);
    setIsSwitching(true);
    void switchTo(target, path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isSwitching, hasTenantParam, startsSwitch, location.key]);

  return startsSwitch || isSwitching;
};
